import React, {useContext, useState, useEffect, useRef} from "react";
import { Grid, Mui, MuiIcons } from "@leap4/frontend";

import {demandWLGeneratorContext} from "./demandWLGeneratorContext";
import {DemandTypes, tableAppAdsTxt_V1Filled} from "../demandWLGenerator.interface";
import {IDemand} from "../../demands/demands/demand.interface";
import {ReportTable} from "../reportTable";
import {FormChipsControlled} from "../../../formChipControl/FormChipsControlled";
import GlobalBundleInput from "../../../modules/demands/base/globalBundleInput";

const { Typography, Button, FormHelperText, MenuItem, Select, TextField, FormControlLabel, Checkbox, Switch, Paper, Divider, Box } = Mui;
type SelectChangeEvent<T = string> = Mui.SelectChangeEvent<T>;
const { ArrowUpward: ArrowUpwardIcon } = MuiIcons;


const GeneratorBuilderView = () => {
    const {
        demandWLGenerator,
        setDemandWLGenerator,
        allDemands,
        runReport,
        publishers,
        reportData,
        reportColumns,
        isLoadingReport,
        validationErrors,
        integrations,
    } = useContext(demandWLGeneratorContext);

    const [demand, setDemand] = useState<IDemand>(allDemands.find(d => d._id === demandWLGenerator.demandId) || {} as IDemand);
    const [selectedIntegrations, setSelectedIntegrations] = useState<string[]>(demandWLGenerator.integrations || []);
    const [integrationInputs, setIntegrationInputs] = useState<string[]>(demandWLGenerator.integrations || []);
    const [integrationTargetListIds, setIntegrationTargetListIds] = useState<{[key: string]: string[]}>(
        demandWLGenerator.integrationTargetListIds || {});
    const [manualBundles, setManualBundles] = useState<string[]>(
        demandWLGenerator.manuallyAddedBundles?.map(bundle => bundle.bundleId) || []);
    const [manualRemovedBundles, setManualRemovedBundles] = useState<string[]>(
        demandWLGenerator.manuallyRemovedBundles?.map(bundle => bundle.bundleId) || []);

    useEffect(() => {
        setSelectedIntegrations(demandWLGenerator.integrations || []);
        setIntegrationInputs(demandWLGenerator.integrations || []);
        setIntegrationTargetListIds(demandWLGenerator.integrationTargetListIds || {});
        setManualBundles(demandWLGenerator.manuallyAddedBundles?.map(bundle => bundle.bundleId) || []);
        setManualRemovedBundles(demandWLGenerator.manuallyRemovedBundles?.map(bundle => bundle.bundleId) || []);
    }, [demandWLGenerator]);

    // Track previous integrations length to detect when integrations are added
    const prevIntegrationsLengthRef = useRef<number>(demandWLGenerator.integrations?.length || 0);
    
    // Separate effect to handle AppIdentifier when integrations are added
    useEffect(() => {
        const currentIntegrationsLength = demandWLGenerator.integrations?.length || 0;
        const prevIntegrationsLength = prevIntegrationsLengthRef.current;
        
        // Only act when integrations go from 0 to >0 (when first integration is added)
        // The ref check prevents infinite loops by only triggering on the transition from 0 to >0
        if (currentIntegrationsLength > 0 && prevIntegrationsLength === 0) {
            // Clear all dimensions and add only AppIdentifier when integrations are first added
            setDemandWLGenerator({
                ...demandWLGenerator,
                dimensions: [{ key: 'AppIdentifier', value: 'AppIdentifier' }]
            });
        }
        
        // Update ref for next render
        prevIntegrationsLengthRef.current = currentIntegrationsLength;
    }, [demandWLGenerator, setDemandWLGenerator]);

    const handleInputChange = (e: any) => {
        const {name, value} = e.target;
        setDemandWLGenerator({...demandWLGenerator, [name]: value});
    };

    const handleDemandChange = (event: any) => {
        const selectedDemand = allDemands.find(
            (d) => d._id === event.target.value
        );
        setDemandWLGenerator({
            ...demandWLGenerator,
            demandId: event.target.value,
            demandPartnerDomain: {
                domain: selectedDemand?.AdvertisingSystem || selectedDemand?.name || "",
                relationship: ["DIRECT","RESELLER"],
            },
            pubAccId: [],
            publishers: [],
        });
        setDemand(selectedDemand || ({} as IDemand));
    };


    const handleIntegrationChange = (index: number) => (event: SelectChangeEvent<string>) => {
        const newIntegrationId = event.target.value;
        const newIntegration = integrations.find(integration => integration._id === newIntegrationId);

        if (newIntegration) {
            const updatedIntegrations = [...selectedIntegrations];
            updatedIntegrations[index] = newIntegrationId;
            setSelectedIntegrations(updatedIntegrations);
            
            const currentTargetListIds = integrationTargetListIds[newIntegrationId] || [];
            const updatedTargetListIds = {
                ...integrationTargetListIds,
                [newIntegrationId]: currentTargetListIds
            };
            setIntegrationTargetListIds(updatedTargetListIds);
            
            // Ensure AppIdentifier is in dimensions (add if not present, keep existing dimensions)
            const hasAppIdentifier = demandWLGenerator.dimensions?.some(d => d.key === 'AppIdentifier');
            const updatedDimensions = hasAppIdentifier 
                ? demandWLGenerator.dimensions || []
                : [{ key: 'AppIdentifier', value: 'AppIdentifier' }, ...(demandWLGenerator.dimensions || [])];
            
            setDemandWLGenerator({
                ...demandWLGenerator,
                integrations: updatedIntegrations,
                integrationTargetListIds: updatedTargetListIds,
                dimensions: updatedDimensions
            });

            const updatedInputs = [...integrationInputs];
            updatedInputs[index] = newIntegrationId;
            setIntegrationInputs(updatedInputs);
        }
    };

    const handleRemoveIntegration = (index: number) => {
        const integrationToRemove = selectedIntegrations[index];
        const updatedIntegrations = selectedIntegrations.filter((_, i) => i !== index);
        setSelectedIntegrations(updatedIntegrations);
        
        const updatedTargetListIds = { ...integrationTargetListIds };
        delete updatedTargetListIds[integrationToRemove];
        setIntegrationTargetListIds(updatedTargetListIds);
        
        setDemandWLGenerator({
            ...demandWLGenerator,
            integrations: updatedIntegrations,
            integrationTargetListIds: updatedTargetListIds,
            updateViaIntegration: updatedIntegrations.length > 0 ? demandWLGenerator.updateViaIntegration : false,
            dimensions: updatedIntegrations.length === 0 ? [] : demandWLGenerator.dimensions
        });

        const updatedInputs = integrationInputs.filter((_, i) => i !== index);
        setIntegrationInputs(updatedInputs);
    };

    const handleAddAnotherIntegration = () => {
        setIntegrationInputs(['', ...integrationInputs]);
        setDemandWLGenerator({
            ...demandWLGenerator,
            integrations: ['', ...(demandWLGenerator.integrations || [])]
        });
    };

    return (
        <div>
            <Typography variant="h3" align="center" sx={{ paddingTop: 3, paddingBottom: 3, fontWeight: 'bold' }}>
                Query Hub
            </Typography>

            <Grid container spacing={3} textAlign="left">
                {/* Template Name - Full width at top */}
                <Grid item xs={12}>
                    <FormHelperText>Template Name</FormHelperText>
                    <TextField
                        fullWidth
                        value={demandWLGenerator.templateName}
                        onChange={handleInputChange}
                        size="small"
                        variant="outlined"
                        name="templateName"
                        error={!!validationErrors.templateName}
                        helperText={validationErrors.templateName}
                    />
                </Grid>

                {/* Dimensions Section - Big and obvious at the top */}
                <Grid item xs={12}>
                    <Paper elevation={2} sx={{ padding: 3, backgroundColor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2, flexWrap: 'wrap' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Dimensions*
                            </Typography>
                            {integrationInputs.length > 0 && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                    Only AppIdentifier dimension currently allowed for templates with integrations!
                                </Typography>
                            )}
                        </Box>
                        <Divider sx={{ marginBottom: 2 }} />
                        <FormChipsControlled
                            valueKey="key"
                            labelKey="value"
                            inputName="Dimensions*"
                            optionsList={tableAppAdsTxt_V1Filled}
                            enableCreate={false}
                            pureCreate={false}
                            onUpdateItems={(items) => {
                                if (selectedIntegrations.length > 0) {
                                    items.unshift({ key: 'AppIdentifier', value: 'AppIdentifier' });
                                    return;
                                }
                                setDemandWLGenerator({
                                    ...demandWLGenerator,
                                    dimensions: items.map((item: any) => ({
                                        key: item,
                                        value: item,
                                    })),
                                });
                            }}
                            disableCloseOnSelect={false}
                            stackProps={{width: "100%"}}
                            controlledValue={demandWLGenerator.dimensions}
                            textFieldProps={{
                                error: !!validationErrors.dimensions,
                                helperText: validationErrors.dimensions,
                                disabled: selectedIntegrations.length > 0,
                                placeholder: selectedIntegrations.length > 0 ? '' : 'Add Item'
                            }}
                            disabled={selectedIntegrations.length > 0}
                            chipStylingFn={(chip) => chip.key === 'AppIdentifier' && selectedIntegrations.length > 0 ? { backgroundColor: "#E0E0E0", color: "#757575", opacity: 0.6, pointerEvents: "none" } : {}}
                        />
                    </Paper>
                </Grid>

                {/* Demand Partner Settings */}
                <Grid item xs={12}>
                    <Paper elevation={2} sx={{ padding: 3, backgroundColor: 'background.paper' }}>
                        <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 'bold' }}>
                            Demand Partner Settings
                        </Typography>
                        <Divider sx={{ marginBottom: 3 }} />
                        <Grid container spacing={1}>
                        {/* Row 1: Demand Partner and Publishers */}
                        <Grid item xs={12} sm={12} lg={4} sx={{ paddingRight: { lg: 0.5 } }}>
                            {/* Demand Partner */}
                            <FormChipsControlled
                                valueKey="_id"
                                labelKey="name"
                                inputName="Demand Partner"
                                optionsList={[
                                    { _id: DemandTypes.WITHOUT_DEMAND_PARTNER_DOMAIN, name: "Without Demand Partner", AdvertisingSystem : ""},
                                    ...allDemands.map(demand => ({
                                        _id: demand._id,
                                        name: demand.name
                                    }))
                                ]}
                                enableCreate={false}
                                pureCreate={true}
                                onUpdateItems={(items) => {
                                    const selectedItem = items[items.length - 1];

                                    if (items.length === 0){
                                        setDemandWLGenerator({
                                            ...demandWLGenerator,
                                            demandId: '',
                                            demandPartnerDomain:{
                                                domain: '',
                                                relationship: ["DIRECT","RESELLER"],
                                            },
                                            pubAccId: [],
                                            publishers: [],
                                        });
                                        setDemand({} as IDemand);
                                        return;
                                    }
                                    
                                    const selectedDemand = allDemands.find(d => d._id === selectedItem);
                                    if (selectedDemand) {
                                        // It's a known demand
                                        handleDemandChange({
                                            target: { value: selectedItem }
                                        });
                                    } else {
                                        // It's a free-form text entry
                                        const customDemand = {
                                            _id: selectedItem,
                                            name: selectedItem,
                                            PubAccId: [],
                                            AdvertisingSystem: selectedItem,
                                            globalBundleWhiteList: [],
                                            globalBundleBlackList: []
                                        };
                                        setDemand(customDemand);
                                        setDemandWLGenerator({
                                            ...demandWLGenerator,
                                            demandId: selectedItem,
                                            demandPartnerDomain: {
                                                domain: selectedItem,
                                                relationship: ["DIRECT", "RESELLER"],
                                            },
                                            pubAccId: [],
                                            publishers: [],
                                        });
                                    }
                                }}
                                disableCloseOnSelect={false}
                                stackProps={{width: "100%"}}
                                controlledValue={demandWLGenerator.demandId ? [{
                                    _id: demandWLGenerator.demandId,
                                    name: allDemands.find(d => d._id === demandWLGenerator.demandId)?.name || 
                                    demandWLGenerator.demandPartnerDomain.domain
                                }] : []}
                            />
                            
                            {/* PubAccId */}
                            {demand?.PubAccId && (
                                <Box sx={{ marginTop: 2 }}>
                                    <FormChipsControlled
                                        valueKey="key"
                                        labelKey="value"
                                        inputName="PubAccId"
                                        optionsList={
                                            demand?.PubAccId?.map((pubAccId) => ({
                                                key: pubAccId.id,
                                                value: `${pubAccId.id} (${pubAccId.description})`,
                                            })) || []
                                        }
                                        enableCreate={false}
                                        pureCreate={true}
                                        onUpdateItems={(items) => {
                                            const updatedItems = items.map((item: any) => {
                                                const matchedPubAcc = demand.PubAccId?.find(
                                                    (pubAccId) => pubAccId.id === item
                                                );
                                                return {
                                                    key: item,
                                                    value: matchedPubAcc
                                                        ? `${matchedPubAcc.id} (${matchedPubAcc.description})`
                                                        : item,
                                                };
                                            });
                                            setDemandWLGenerator({
                                                ...demandWLGenerator,
                                                pubAccId: updatedItems,
                                            });
                                        }}
                                        disableCloseOnSelect={false}
                                        stackProps={{width: "100%"}}
                                        controlledValue={demandWLGenerator.pubAccId}
                                    />
                                </Box>
                            )}
                            
                            {/*Demand Partner Domain Settings block */}
                            {demand.AdvertisingSystem && (
                                <Grid container spacing={2} sx={{ marginTop: 0 }}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" marginTop={0} marginBottom={0} sx={{ textDecoration: 'underline' }}>
                                            Demand Partner Domain Settings {demandWLGenerator.demandPartnerDomain.domain && `(${demandWLGenerator.demandPartnerDomain.domain.split('.')[0]})`}:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <FormHelperText>Relationship:</FormHelperText>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    disabled={demandWLGenerator.demandId === DemandTypes.WITHOUT_DEMAND_PARTNER_DOMAIN}
                                                    checked={demandWLGenerator.demandPartnerDomain?.relationship?.includes("RESELLER") || false}
                                                    onChange={(e) => {
                                                        const isChecked = e.target.checked;
                                                        const currentRelationships = demandWLGenerator.demandPartnerDomain.relationship || [];
                                                        if (!isChecked && currentRelationships.length <= 1) return;
                                                        setDemandWLGenerator({
                                                            ...demandWLGenerator,
                                                            demandPartnerDomain: {
                                                                ...demandWLGenerator.demandPartnerDomain,
                                                                relationship: isChecked
                                                                    ? [...currentRelationships, "RESELLER"]
                                                                    : currentRelationships.filter((r) => r !== "RESELLER"),
                                                            },
                                                        });
                                                    }}
                                                />
                                            }
                                            label="Reseller"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    disabled={demandWLGenerator.demandId === DemandTypes.WITHOUT_DEMAND_PARTNER_DOMAIN}
                                                    checked={demandWLGenerator.demandPartnerDomain?.relationship?.includes("DIRECT") || false}
                                                    onChange={(e) => {
                                                        const isChecked = e.target.checked;
                                                        const currentRelationships = demandWLGenerator.demandPartnerDomain.relationship || [];
                                                        if (!isChecked && currentRelationships.length <= 1) return;
                                                        setDemandWLGenerator({
                                                            ...demandWLGenerator,
                                                            demandPartnerDomain: {
                                                                ...demandWLGenerator.demandPartnerDomain,
                                                                relationship: isChecked
                                                                    ? [...currentRelationships, "DIRECT"]
                                                                    : currentRelationships.filter((r) => r !== "DIRECT"),
                                                            },
                                                        });
                                                    }}
                                                />
                                            }
                                            label="Direct"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={demandWLGenerator.excludeGlobalBundleList || false}
                                                    disabled={!allDemands.some(d => d._id === demandWLGenerator.demandId)}
                                                    onChange={(e) =>
                                                        setDemandWLGenerator({
                                                            ...demandWLGenerator,
                                                            excludeGlobalBundleList: e.target.checked,
                                                        })
                                                    }
                                                />
                                            }
                                            label="Exclude Global Bundle List"
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={12} lg={8} sx={{ paddingLeft: { lg: 0.5 } }}>
                            {/* Publishers */}
                            <Box sx={{
                                width: '100%',
                                '& .MuiAutocomplete-root': {
                                    width: '100%',
                                },
                                '& .MuiStack-root': {
                                    width: '100% !important',
                                },
                                '& .MuiAutocomplete-inputRoot': {
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    '& .MuiAutocomplete-tag': {
                                        margin: '2px 4px',
                                        flexShrink: 0,
                                    },
                                    '& .MuiInputBase-input': {
                                        minWidth: '150px !important',
                                        flex: '1 1 auto',
                                    }
                                },
                                '& .MuiAutocomplete-input': {
                                    width: '0 !important',
                                    minWidth: '150px',
                                    flexGrow: 1,
                                }
                            }}>
                                <FormChipsControlled
                                    placeholder={"Select Publishers or add domain manually"}
                                    valueKey="key"
                                    labelKey="value"
                                    inputName="Publishers"
                                    optionsList={demandWLGenerator.publishers.length === publishers.length
                                        ? publishers.map(({ domain, name }) => ({ key: domain, value: name }))
                                        : [{ key: 'all', value: 'All Publishers' }, ...publishers.map(({ domain, name }) => ({ key: domain, value: name }))]}
                                    enableCreate={false}
                                    pureCreate={false}
                                    onUpdateItems={(items) => {
                                        console.log(items);
                                        if (items.includes('all')) {
                                            // If "All" is selected, set all publishers
                                            setDemandWLGenerator({
                                                ...demandWLGenerator,
                                                publishers: publishers.map((publisher) => ({
                                                    key: publisher.domain,
                                                    value: publisher.name,
                                                })),
                                            });
                                        } else {
                                            setDemandWLGenerator({
                                                ...demandWLGenerator,
                                                publishers: items.map((item: any) => {
                                                    if (item.includes(',')) {
                                                        return item.split(',').map((subItem: string) => ({
                                                            key: subItem.trim(),
                                                            value: publishers.find(value => value.domain === subItem.trim())?.name || subItem.trim(),
                                                        }));
                                                    }
                                                    return {
                                                        key: item,
                                                        value: publishers.find(value => value.domain === item)?.name || item,
                                                    };
                                                }).flat(),
                                            });
                                        }
                                    }}
                                    disableCloseOnSelect={false}
                                    stackProps={{width: "100%"}}
                                    controlledValue={demandWLGenerator.publishers}
                                />
                            </Box>
                        </Grid>
                        </Grid>
                        
                        {/* Toggle switches - separate container, fixed position */}
                        <Box sx={{ marginTop: 3, borderTop: '1px solid', borderColor: 'divider', paddingTop: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={demandWLGenerator.includePublishers || false}
                                        onChange={(e) =>
                                            setDemandWLGenerator({
                                                ...demandWLGenerator,
                                                includePublishers: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label={demandWLGenerator.includePublishers ? "Include Publishers" : "Exclude Publishers"}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={demandWLGenerator.searchPublisherOnDomain || false}
                                        disabled={demandWLGenerator.includeSiteDomainAdsTxtLine}
                                        onChange={(e) =>
                                            setDemandWLGenerator({
                                                ...demandWLGenerator,
                                                searchPublisherOnDomain: e.target.checked,
                                                searchPublisherOnSourceDomain: !e.target.checked ? true : demandWLGenerator.searchPublisherOnSourceDomain,
                                            })
                                        }
                                    />
                                }
                                label={"Search O&O"}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={demandWLGenerator.searchPublisherOnSourceDomain || false}
                                        disabled={demandWLGenerator.includeSiteDomainAdsTxtLine}
                                        onChange={(e) =>
                                            setDemandWLGenerator({
                                                ...demandWLGenerator,
                                                searchPublisherOnSourceDomain: e.target.checked,
                                                searchPublisherOnDomain: !e.target.checked ? true : demandWLGenerator.searchPublisherOnDomain,
                                            })
                                        }
                                    />
                                }
                                label={"Search IPD"}
                            />
                        </Box>
                    </Paper>
                </Grid>

                {/* Manual Bundles and Integrations Section - Show when integrations array exists (even if empty string for new integration) */}
                {integrationInputs.length > 0 && (
                    <>
                        {/* Manual Bundle Management - Left side */}
                        <Grid item xs={12} md={6} lg={6}>
                            <Paper elevation={2} sx={{ padding: 3, backgroundColor: 'background.paper', height: '100%' }}>
                                <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 'bold' }}>
                                    Manual Bundle Management
                                </Typography>
                                <Divider sx={{ marginBottom: 3 }} />
                                
                                <Grid container spacing={3}>
                                    {/* Add Bundles Section */}
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ marginBottom: 1.5, fontWeight: 'bold', color: 'success.main' }}>
                                            Add Extra BundleIds
                                        </Typography>
                                        <GlobalBundleInput
                                            globalBundleList={manualBundles}
                                            setBundleList={(items) => {
                                                setManualBundles(items);
                                                setDemandWLGenerator({
                                                    ...demandWLGenerator,
                                                    manuallyAddedBundles: items.map(bundleId => ({ bundleId }))
                                                });
                                            }}
                                            title="Bundle IDs"
                                            showExportButton={false}
                                            error={''}
                                            setError={(error) => {
                                                //Handle error if needed
                                            }}
                                        />
                                    </Grid>
                                    
                                    {/* Remove Bundles Section */}
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ marginBottom: 1.5, fontWeight: 'bold', color: 'error.main' }}>
                                            Remove BundleIds
                                        </Typography>
                                        <GlobalBundleInput
                                            globalBundleList={manualRemovedBundles}
                                            setBundleList={(items) => {
                                                setManualRemovedBundles(items);
                                                setDemandWLGenerator({
                                                    ...demandWLGenerator,
                                                    manuallyRemovedBundles: items.map(bundleId => ({ bundleId }))
                                                });
                                            }}
                                            title="Bundle IDs"
                                            showExportButton={false}
                                            error={''}
                                            setError={(error) => {
                                                //Handle error if needed
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Integration Section - Right side */}
                        <Grid item xs={12} md={6} lg={6}>
                            <Paper elevation={2} sx={{ padding: 3, width: '100%', minHeight: '100%', maxHeight: '800px', overflowY: 'auto', backgroundColor: 'background.paper' }}>
                                <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 'bold' }}>
                                    Auto update via integrations ({integrationInputs.length} Active)
                                </Typography>
                                <Divider sx={{ marginBottom: 2 }} />
                                {integrationInputs.length > 0 && (
                                    <Button variant="outlined" onClick={handleAddAnotherIntegration} sx={{ marginBottom: 2, width: '100%', padding: '8px 16px' }}>
                                        Add Another Integration
                                    </Button>
                                )}
                                {integrationInputs.map((inputValue, index) => (
                                    <Box key={index} sx={{ marginTop: index > 0 ? 1.5 : 0, marginBottom: 1.5, padding: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: 1.5 }}>
                                        <Button variant="outlined" color="secondary" onClick={() => handleRemoveIntegration(index)} sx={{ marginRight: 1.5, minWidth: '80px', padding: '6px 16px' }}>
                                            Remove
                                        </Button>
                                            <Select
                                                value={inputValue || ''}
                                                onChange={handleIntegrationChange(index)}
                                                variant="outlined"
                                                title="Select Integration"
                                                fullWidth
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (!selected || selected === '') {
                                                        return <em style={{ color: '#999' }}>Select an Integration</em>;
                                                    }
                                                    const integration = integrations.find(i => i._id === selected);
                                                    return integration ? `${integration.name} (${integration.adserver})` : selected;
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Select an Integration</em>
                                                </MenuItem>
                                                {integrations
                                                    .filter(integration => 
                                                        !selectedIntegrations.includes(integration._id) || 
                                                        integration._id === inputValue
                                                    )
                                                    .map((integration) => (
                                                        <MenuItem key={integration._id} value={integration._id}>
                                                            {integration.name} ({integration.adserver})
                                                        </MenuItem>
                                                    ))
                                                }
                                            </Select>
                                    </Box>
                                    
                                        {inputValue && (
                                            <Box sx={{ width: '100%', marginTop: 1.5 }}>
                                                <GlobalBundleInput
                                                globalBundleList={integrationTargetListIds[inputValue] || []}
                                                setBundleList={(items) => {
                                                    const updatedMapping = {
                                                        ...integrationTargetListIds,
                                                        [inputValue]: items
                                                    };
                                                    setIntegrationTargetListIds(updatedMapping);
                                                    setDemandWLGenerator({
                                                        ...demandWLGenerator,
                                                        integrationTargetListIds: updatedMapping
                                                    });
                                                    }}
                                                    title="Target List IDs"
                                                    showExportButton={false}
                                                    error={''}
                                                    setError={(error) => {
                                                        //Handle error if needed
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Paper>
                        </Grid>
                    </>
                )}

                {/* Organization Settings */}
                <Grid item xs={12}>
                    <Paper elevation={2} sx={{ padding: 3, backgroundColor: 'background.paper' }}>
                        <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 'bold' }}>
                            Organization Settings ({demandWLGenerator.siteDomain?.domain?.split('.')[0] || 'N/A'})
                        </Typography>
                        <Divider sx={{ marginBottom: 3 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={demandWLGenerator.includeSiteDomainAdsTxtLine || false}
                                            onChange={(e) =>
                                                setDemandWLGenerator({
                                                    ...demandWLGenerator,
                                                    searchPublisherOnSourceDomain: e.target.checked===true ? true : demandWLGenerator.searchPublisherOnSourceDomain,
                                                    searchPublisherOnDomain: e.target.checked===true ? true : demandWLGenerator.searchPublisherOnDomain,
                                                    includeSiteDomainAdsTxtLine: e.target.checked,
                                                })
                                            }
                                        />
                                    }
                                    label={`Include (${demandWLGenerator.siteDomain?.domain || ''}) ads.txt line`}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormHelperText>Organization Domain</FormHelperText>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                demandWLGenerator.siteDomain?.includeInventoryPartnerDomain ||
                                                false
                                            }
                                            onChange={(e) =>
                                                setDemandWLGenerator({
                                                    ...demandWLGenerator,
                                                    siteDomain: {
                                                        ...demandWLGenerator.siteDomain,
                                                        includeInventoryPartnerDomain: e.target.checked,
                                                    },
                                                })
                                            }
                                            disabled={!demandWLGenerator.includeSiteDomainAdsTxtLine}
                                        />
                                    }
                                    label="Include Inventory Partner Domain"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormHelperText>Relationship</FormHelperText>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            disabled={!demandWLGenerator.includeSiteDomainAdsTxtLine}
                                            checked={
                                                demandWLGenerator.siteDomain?.relationship?.includes(
                                                    "RESELLER"
                                                ) || false
                                            }
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                const currentRelationships = demandWLGenerator.siteDomain.relationship || [];
                                                if (!isChecked && currentRelationships.length <= 1) {
                                                    return;
                                                }
                                                setDemandWLGenerator({
                                                    ...demandWLGenerator,
                                                    siteDomain: {
                                                        ...demandWLGenerator.siteDomain,
                                                        relationship: isChecked
                                                            ? [
                                                                ...currentRelationships,
                                                                "RESELLER",
                                                            ]
                                                            : currentRelationships.filter(
                                                                (r) => r !== "RESELLER"
                                                            ),
                                                    },
                                                });
                                            }}
                                        />
                                    }
                                    label="Reseller"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            disabled={!demandWLGenerator.includeSiteDomainAdsTxtLine}
                                            checked={
                                                demandWLGenerator.siteDomain?.relationship?.includes(
                                                    "DIRECT"
                                                ) || false
                                            }
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                const currentRelationships = demandWLGenerator.siteDomain.relationship || [];
                                                if (!isChecked && currentRelationships.length <= 1) {
                                                    return;
                                                }
                                                setDemandWLGenerator({
                                                    ...demandWLGenerator,
                                                    siteDomain: {
                                                        ...demandWLGenerator.siteDomain,
                                                        relationship: isChecked
                                                            ? [
                                                                ...currentRelationships,
                                                                "DIRECT",
                                                            ]
                                                            : currentRelationships.filter((r) => r !== "DIRECT"),
                                                    },
                                                });
                                            }}
                                        />
                                    }
                                    label="Direct"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Run Report Button and Scroll to Top */}
                <Grid item xs={12} marginTop={2}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button variant="outlined" onClick={()=>runReport(demandWLGenerator)} color="success" disabled={isLoadingReport}>
                            Run Report
                        </Button>
                        <Button
                            startIcon={<ArrowUpwardIcon />}
                            variant="outlined"
                            color="primary"
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        >
                            Scroll to Top
                        </Button>
                    </Box>
                </Grid>
                {isLoadingReport && (
                    <Grid item xs={12} marginTop={2}>
                        <Typography variant="body2">Loading...</Typography>
                    </Grid>
                )}
                {
                    reportData?.length === 0 && (
                        <Grid item xs={12} marginTop={2}>
                            <Typography variant="body2">No data found</Typography>
                        </Grid>
                    )
                }
                {reportData && (
                    <Grid item xs={12} marginTop={2}>
                        <ReportTable data={reportData} columns={reportColumns}/>
                    </Grid>
                )}
            </Grid>
        </div>
    );
};

export default GeneratorBuilderView;
